import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ProductRow } from '../../../../lib/supabase';
import { categories } from '../../../../mocks/products';
import ImageUploader from './ImageUploader';

interface Props {
  product: ProductRow | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  category_id: string;
  sub_category_id: string;
  title: string;
  model: string;
  image: string;
  tags: string;
  description: string;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
}

const defaultForm: FormData = {
  category_id: '',
  sub_category_id: '',
  title: '',
  model: '',
  image: '',
  tags: '',
  description: '',
  is_active: true,
  is_featured: false,
  sort_order: 0,
};

export default function ProductFormModal({ product, onClose, onSaved }: Props) {
  const isEdit = !!product;
  const [form, setForm] = useState<FormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  useEffect(() => {
    if (product) {
      setForm({
        category_id: product.category_id,
        sub_category_id: product.sub_category_id,
        title: product.title,
        model: product.model,
        image: product.image,
        tags: product.tags.join(', '),
        description: product.description,
        is_active: product.is_active,
        is_featured: product.is_featured,
        sort_order: product.sort_order,
      });
    } else {
      setForm(defaultForm);
    }
  }, [product]);

  const selectedCat = categories.find(c => c.id === form.category_id);

  const validate = (): boolean => {
    const e: Partial<FormData> = {};
    if (!form.title.trim()) e.title = '請輸入產品名稱';
    if (!form.model.trim()) e.model = '請輸入產品型號';
    if (!form.category_id) e.category_id = '請選擇分類';
    if (!form.description.trim()) e.description = '請輸入產品描述';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    const payload = {
      category_id: form.category_id,
      sub_category_id: form.sub_category_id,
      title: form.title.trim(),
      model: form.model.trim(),
      image: form.image.trim(),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      description: form.description.trim(),
      is_active: form.is_active,
      is_featured: form.is_featured,
      sort_order: Number(form.sort_order),
      updated_at: new Date().toISOString(),
    };

    let error;
    if (isEdit && product) {
      ({ error } = await supabase.from('products').update(payload).eq('id', product.id));
    } else {
      ({ error } = await supabase.from('products').insert([payload]));
    }

    if (!error) {
      onSaved();
      onClose();
    }
    setSaving(false);
  };

  const set = (key: keyof FormData, val: string | boolean | number) =>
    setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 pb-8 px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#1a2535]">
          <h3 className="text-white font-semibold text-sm">{isEdit ? '編輯產品' : '新增產品'}</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* 基本資訊 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                產品名稱 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder="例：SS304 防水接線箱"
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
              />
              {errors.title && <p className="text-xs text-red-400 mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                產品型號 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.model}
                onChange={e => set('model', e.target.value)}
                placeholder="例：SS304-IP66-W"
                className="w-full text-sm font-mono border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
              />
              {errors.model && <p className="text-xs text-red-400 mt-1">{errors.model}</p>}
            </div>
          </div>

          {/* 分類 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                主分類 <span className="text-red-400">*</span>
              </label>
              <select
                value={form.category_id}
                onChange={e => { set('category_id', e.target.value); set('sub_category_id', ''); }}
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] bg-white cursor-pointer"
              >
                <option value="">請選擇主分類</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              {errors.category_id && <p className="text-xs text-red-400 mt-1">{errors.category_id}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">子分類</label>
              <select
                value={form.sub_category_id}
                onChange={e => set('sub_category_id', e.target.value)}
                disabled={!selectedCat}
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] bg-white cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">請選擇子分類</option>
                {selectedCat?.subCategories.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags + 排序 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                標籤 <span className="text-gray-400 font-normal">（逗號分隔）</span>
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={e => set('tags', e.target.value)}
                placeholder="例：IP66, SS304, ATEX"
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">排序（數字越小越前）</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={e => set('sort_order', Number(e.target.value))}
                min={0}
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
              />
            </div>
          </div>

          {/* 產品描述 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              產品描述 <span className="text-red-400">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              placeholder="輸入產品詳細描述..."
              className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] resize-none"
            />
            {errors.description && <p className="text-xs text-red-400 mt-1">{errors.description}</p>}
          </div>

          {/* 產品圖片 */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">產品圖片</label>
            <ImageUploader
              value={form.image}
              onChange={url => set('image', url)}
            />
          </div>

          {/* 上架狀態 & 熱門產品 */}
          <div className="flex flex-col gap-3 py-3 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => set('is_active', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-checked:bg-[#1a73b5] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:transition-all peer-checked:after:translate-x-4" />
              </label>
              <span className="text-sm text-gray-600">
                {form.is_active ? '已上架（前台可見）' : '已下架（前台隱藏）'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={e => set('is_featured', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-checked:bg-amber-400 transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:transition-all peer-checked:after:translate-x-4" />
              </label>
              <span className="text-sm text-gray-600 flex items-center gap-1.5">
                <i className={`ri-star-line text-sm ${form.is_featured ? 'text-amber-400' : 'text-gray-300'}`} />
                {form.is_featured ? '顯示於首頁熱門產品' : '不顯示於首頁熱門產品'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm bg-[#1a73b5] text-white hover:bg-[#1558a0] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {saving ? '儲存中...' : isEdit ? '儲存變更' : '新增產品'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
